import { milliToPercent, percentToMilli } from '@lib/helpers/sales-money'
import type { ScheduleAmount, ScheduleDue, ScheduleEntry } from '@lib/helpers/sales-schedule'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { centsToInput, inputToCents } from '@/lib/price'

/** The amount and due discriminants from the shared schedule, not a second list. */
export type AmountKind = ScheduleAmount['kind']
export type DueKind = ScheduleDue['kind']

/** A payment as it's being typed: amounts and days as text until saved. */
export interface EntryDraft {
  key: string
  label: string
  amountKind: AmountKind
  /** Dollars for a fixed amount, a percentage for a percent. */
  amount: string
  dueKind: DueKind
  days: string
  /** Only a proposal's own schedule has fixed dates. */
  date: CalendarDay | ''
}

let counter = 0
export function newKey(): string {
  counter += 1
  return `new-${String(counter)}`
}

export function entryDraft(e: ScheduleEntry): EntryDraft {
  let amount = ''
  if (e.amount.kind === 'fixed') amount = centsToInput(e.amount.cents)
  if (e.amount.kind === 'percent') amount = milliToPercent(e.amount.milli)
  return {
    key: newKey(),
    label: e.label,
    amountKind: e.amount.kind,
    amount,
    dueKind: e.due.kind,
    days: e.due.kind === 'before_event' ? String(e.due.days) : '30',
    date: e.due.kind === 'on_date' ? e.due.date : '',
  }
}

/** A draft as a schedule entry, or why it isn't one yet. */
export function entryOf(d: EntryDraft): ScheduleEntry | string {
  const label = d.label === '' ? 'every payment' : d.label
  let amount: ScheduleEntry['amount'] = { kind: 'rest' }
  if (d.amountKind === 'fixed') {
    const cents = inputToCents(d.amount)
    if (cents === null || cents === 0) return `Enter an amount in dollars for ${label}.`
    amount = { kind: 'fixed', cents }
  }
  if (d.amountKind === 'percent') {
    const milli = percentToMilli(d.amount)
    if (milli === null || milli === 0) return `Enter a percentage for ${label}.`
    amount = { kind: 'percent', milli }
  }
  if (d.dueKind === 'on_date') {
    if (d.date === '') return `Choose the day ${label} is due.`
    return { label: d.label, amount, due: { kind: 'on_date', date: d.date } }
  }
  const days = Number.parseInt(d.days, 10)
  if (d.dueKind === 'before_event' && (!Number.isInteger(days) || days < 0 || days > 730)) return `Enter how many days before the event ${label} is due.`
  return { label: d.label, amount, due: d.dueKind === 'before_event' ? { kind: 'before_event', days } : { kind: 'on_signing' } }
}

/** Every draft as an entry, or the first reason one isn't. */
export function entriesOf(drafts: readonly EntryDraft[]): ScheduleEntry[] | string {
  const entries: ScheduleEntry[] = []
  for (const d of drafts) {
    const entry = entryOf(d)
    if (typeof entry === 'string') return entry
    entries.push(entry)
  }
  return entries
}

export const blankEntry = (label: string, amountKind: AmountKind): EntryDraft => ({
  key: newKey(),
  label,
  amountKind,
  amount: '',
  dueKind: amountKind === 'rest' ? 'before_event' : 'on_signing',
  days: '30',
  date: '',
})
